import type { SetRequired } from 'type-fest'
import { safeParseFloat, safeParseInt } from './number'
import type { core, GlobalMeta, util } from 'zod'
import { z } from 'zod'

export const MoneyAmountSchema = z
  .union([z.string({ error: 'Required' }), z.number({ error: 'Required' })])
  .transform((value) => {
    return safeParseFloat(value, 0).toFixed(2)
  })

export const IntSchema = z
  .union([z.string(), z.number()])
  .transform((value) => {
    return safeParseInt(value, 0)
  })

export function createRequiredDateSchema() {
  return z.iso.date({ error: 'Required' }).transform((value, ctx) => {
    if (value === undefined) {
      ctx.addIssue({
        code: 'custom',
        input: value,
        message: 'Required',
      })

      return z.NEVER
    }

    return value
  })
}

export const OtherOptionValue = 'other'

export function createRequiredStringSchema({
  error = 'Required',
}: { error?: string } = {}) {
  return z.string({ error }).min(1, { error })
}

export function createRequiredStringOrOtherSchema({
  error = 'Required',
}: { error?: string } = {}) {
  return z
    .object({
      other: z.string().optional().meta({ description: 'Other value' }),
      value: createRequiredStringSchema({ error }),
    })
    .refine(
      (data) => {
        return data.value !== OtherOptionValue || !!data.other
      },
      {
        message: error,
        path: ['other'],
      },
    )
}

export function createRequiredRichTextStringSchema({
  error = 'Required',
}: { error?: string } = {}) {
  return z
    .string()
    .trim()
    .transform((value) => {
      // trim zero-width characters from start and end
      return (
        value
          // oxlint-disable-next-line no-misleading-character-class
          .replace(/^[\u200B\u200C\u200D\uFEFF\u200E\u200F]+/, '')
          // oxlint-disable-next-line no-misleading-character-class
          .replace(/[\u200B\u200C\u200D\uFEFF\u200E\u200F]+$/, '')
      )
    })
    .pipe(
      z
        .string()
        .min(1, { message: error })
        .max(10_000, { message: 'Cannot exceed 10,000 characters' }),
    )
}

export type EnumValueMeta = SetRequired<
  Pick<GlobalMeta, 'id' | 'title' | 'description'>,
  'title'
>

type LabeledEnumDefinition<
  T extends util.Literal,
  Meta extends EnumValueMeta = EnumValueMeta,
> = Omit<Meta, 'value'> & {
  value: T
}

export interface EnumSchemaOption<T extends util.Literal> {
  label: string
  subtitle?: string
  value: T
}

/**
 * wrapper around a zod enum-like union with O(1) metadata lookups
 */
export function createEnumSchema<
  T extends util.Literal,
  Meta extends EnumValueMeta = EnumValueMeta,
>(
  definitions: ReadonlyArray<LabeledEnumDefinition<T, Meta>>,
  params?: core.$ZodUnionParams,
) {
  const metaMap = new Map<T, Meta>()
  const literalMap = new Map<T, z.ZodLiteral<T>>()
  const options: Array<z.ZodLiteral<T>> = []

  for (const definition of definitions) {
    const { value, ...meta } = definition
    const untypedMeta: unknown = meta
    // SAFETY: Meta extends EnumValueMeta with no `value` field, so the rest object is Meta.
    metaMap.set(value, untypedMeta as Meta)
    const literal = z.literal(value, { error: 'Required' }).meta(meta)
    literalMap.set(value, literal)
    options.push(literal)
  }

  const schema = z.union(options, params)

  const getSafeMeta = <Str extends string>(value: Str): Meta | undefined => {
    const parsed = schema.safeParse(value)
    return parsed.success ? metaMap.get(parsed.data) : undefined
  }

  const getMeta = (value: T): Meta => {
    const meta = metaMap.get(value)
    if (meta === undefined) {
      throw new Error(`missing meta for value: ${value}`)
    }

    return meta
  }

  return {
    definitions,

    literal<V extends T>(value: V): z.ZodLiteral<V> {
      const literal = literalMap.get(value)
      if (literal === undefined) {
        throw new Error(`missing literal for value: ${value}`)
      }

      // SAFETY: Map.get cannot express that key V returns ZodLiteral<V>.
      return literal as z.ZodLiteral<V>
    },

    meta: getMeta,

    options(): Array<EnumSchemaOption<T>> {
      return options.map((option) => {
        const meta = getMeta(option.value)
        return {
          label: meta.title,
          subtitle: meta.description,
          value: option.value,
        }
      })
    },

    safeMeta: getSafeMeta,

    schema,

    values() {
      return Array.from(metaMap.keys())
    },
  }
}

interface CreateRequiredOrOtherEnumSchemaParams<
  OtherValue extends string = typeof OtherOptionValue,
> {
  error?: string
  /**
   * @default { title: 'Other'}
   */
  otherMeta?: EnumValueMeta
  /**
   * @default 'other'
   */
  otherValue?: OtherValue
}

export function createRequiredOrOtherEnumSchema<
  T extends string,
  OtherValue extends string = typeof OtherOptionValue,
>(
  definitions: ReadonlyArray<LabeledEnumDefinition<T>>,
  {
    error = 'Required',
    otherMeta = { title: 'Other' },
    // SAFETY: default OtherValue is typeof OtherOptionValue when otherValue is omitted.
    otherValue = OtherOptionValue as OtherValue,
  }: CreateRequiredOrOtherEnumSchemaParams<OtherValue> = {},
) {
  const _enum = createEnumSchema<T | OtherValue>([
    ...definitions,
    { value: otherValue, ...otherMeta },
  ])

  return {
    enum: _enum,
    schema: z
      .object({
        other: z
          .string()
          .meta({
            description:
              'If the value is "other", this field contains custom text input - otherwise it is empty.',
          })
          .optional(),
        value: _enum.schema,
      })
      .refine(
        (data) => {
          return data.value !== otherValue || !!data.other
        },
        {
          message: error,
          path: ['other'],
        },
      ),
  }
}

export function toLLMFriendlyJsonSchema(schema: z.ZodSchema) {
  return z.toJSONSchema(schema, {
    io: 'input',
    unrepresentable: 'any',
  })
}

const UuidSchema = z.uuid()
export function isUuid(value: unknown) {
  return UuidSchema.safeParse(value).success
}
